import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  ChevronRight, ChevronDown, TrendingUp, TrendingDown, Minus,
  DollarSign, Users, Settings, GraduationCap, AlertTriangle,
  MessageSquare, ArrowUpRight, ArrowDownRight, BarChart3
} from 'lucide-react';
import { 
  bscPerspectives, mockObjectives, calculateEntityScore, 
  calculatePerspectiveScore, getStatusColor 
} from '../../mock/strategyData';
import SectionHeader from '../shared/SectionHeader';

// Icon map
const iconMap = {
  DollarSign, Users, Settings, GraduationCap
};

// Trend Icon Component
const TrendIcon = ({ trend, className = '' }) => {
  if (trend === 'up') return <TrendingUp className={`w-4 h-4 text-emerald-500 ${className}`} />;
  if (trend === 'down') return <TrendingDown className={`w-4 h-4 text-red-500 ${className}`} />;
  return <Minus className={`w-4 h-4 text-muted-foreground ${className}`} />;
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    'exceeding': { label: 'Exceeding', className: 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30' },
    'on-track': { label: 'On Track', className: 'bg-blue-500/20 text-blue-500 border-blue-500/30' },
    'at-risk': { label: 'At Risk', className: 'bg-amber-500/20 text-amber-500 border-amber-500/30' },
    'below-target': { label: 'Below Target', className: 'bg-orange-500/20 text-orange-500 border-orange-500/30' },
    'critical': { label: 'Critical', className: 'bg-red-500/20 text-red-500 border-red-500/30' }
  };
  
  const { label, className } = config[status] || config['on-track'];
  
  return (
    <Badge variant="outline" className={className}>
      {label}
    </Badge>
  );
};

// Score Gauge Component
const ScoreGauge = ({ score, label, perspectiveColor }) => {
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-28 h-28">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            className="text-muted/20"
          />
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={perspectiveColor}
            strokeWidth="8"
            strokeDasharray={circumference}
            strokeDashoffset={circumference - progress}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold text-foreground">{score}%</span>
        </div>
      </div>
      <span className="mt-2 text-sm font-medium text-foreground text-center">{label}</span>
    </div>
  );
};

// Objective Row Component
const ObjectiveRow = ({ objective, isExpanded, onToggle }) => {
  const achievement = objective.target > 0 
    ? Math.round((objective.actual / objective.target) * 100) 
    : 0;
  
  const getAchievementColor = (ach) => {
    if (ach >= 100) return 'text-emerald-500';
    if (ach >= 90) return 'text-blue-500';
    if (ach >= 75) return 'text-amber-500';
    return 'text-red-500';
  };

  return (
    <div className="border-b border-border/50 last:border-0">
      <div 
        className="flex items-center py-3 px-4 hover:bg-muted/20 transition-colors cursor-pointer"
        onClick={onToggle}
        data-testid={`objective-row-${objective.id}`}
      >
        <div className="w-8">
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-[200px]">
          <span className="font-medium text-foreground">{objective.name}</span>
        </div>
        <div className="w-20 text-center text-sm text-muted-foreground">
          {objective.weight}%
        </div>
        <div className="w-20 text-center text-sm text-muted-foreground">
          {objective.bonus?.toFixed(2) || '-'}%
        </div>
        <div className="w-24 text-center text-sm text-muted-foreground">
          {objective.lastYearActual !== undefined ? `${objective.lastYearActual}${objective.unit}` : '-'}
        </div>
        <div className="w-24 text-center text-sm font-medium text-foreground">
          {objective.actual}{objective.unit}
        </div>
        <div className="w-24 text-center text-sm text-muted-foreground">
          {objective.target}{objective.unit}
        </div>
        <div className="w-16 flex justify-center">
          <StatusBadge status={objective.status} />
        </div>
        <div className="w-12 flex justify-center">
          <TrendIcon trend={objective.trend} />
        </div>
        <div className={`w-24 text-center text-sm font-bold ${getAchievementColor(achievement)}`}>
          {achievement}%
        </div>
        <div className="w-12 flex justify-center">
          {objective.remarks && (
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              <MessageSquare className="w-4 h-4 text-muted-foreground" />
            </Button>
          )}
        </div>
      </div>
      
      {isExpanded && (
        <div className="px-12 py-3 bg-muted/10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Description:</span>
              <p className="text-foreground mt-1">KPI measuring {objective.name.toLowerCase()} performance</p>
            </div>
            <div>
              <span className="text-muted-foreground">Calculation:</span>
              <p className="text-foreground mt-1">(Actual / Target) × 100</p>
            </div>
            <div>
              <span className="text-muted-foreground">Data Source:</span>
              <p className="text-foreground mt-1">Finance System / ERP</p>
            </div>
            <div>
              <span className="text-muted-foreground">Owner:</span>
              <p className="text-foreground mt-1">Department Head</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CompanyScorecard = ({ entity }) => {
  const [expandedObjectives, setExpandedObjectives] = useState([]);
  const [expandedPerspectives, setExpandedPerspectives] = useState(bscPerspectives.map(p => p.id));

  // Get entity objectives
  const entityObjectives = useMemo(() => {
    return mockObjectives.filter(
      obj => obj.entityId === entity.id && obj.entityType === entity.entityType
    );
  }, [entity]);

  // Calculate overall score
  const overallScore = useMemo(() => {
    return calculateEntityScore(entity.id, entity.entityType);
  }, [entity]);

  // Calculate perspective scores
  const perspectiveScores = useMemo(() => {
    const scores = {};
    bscPerspectives.forEach(p => {
      scores[p.id] = calculatePerspectiveScore(entity.id, entity.entityType, p.id);
    });
    return scores;
  }, [entity]);

  // Group objectives by perspective
  const objectivesByPerspective = useMemo(() => {
    const grouped = {};
    bscPerspectives.forEach(p => {
      grouped[p.id] = entityObjectives.filter(obj => obj.perspectiveId === p.id);
    });
    return grouped;
  }, [entityObjectives]);

  const toggleObjective = (id) => {
    setExpandedObjectives(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const togglePerspective = (id) => {
    setExpandedPerspectives(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Calculate totals
  const totals = useMemo(() => {
    const totalWeight = entityObjectives.reduce((sum, obj) => sum + obj.weight, 0);
    const totalBonus = entityObjectives.reduce((sum, obj) => sum + (obj.bonus || 0), 0);
    return { weight: totalWeight, bonus: totalBonus };
  }, [entityObjectives]);

  return (
    <div className="space-y-6">
      {/* Header Card with Overall Score */}
      <SectionHeader
        title={`${entity.name} Scorecard`}
        icon={BarChart3}
        iconColor="text-teal-500"
        gradient="from-teal-500/10 via-cyan-500/10 to-blue-500/10"
        actions={
          <>
            <Badge variant="secondary">{totals.weight.toFixed(1)}% Weight</Badge>
            <Badge variant="outline">{totals.bonus.toFixed(2)}% Bonus</Badge>
          </>
        }
      >
        <div className="flex flex-col lg:flex-row items-center gap-8">
          {/* Entity Info & Overall Score */}
          <div className="flex items-center gap-6">
            <div className="relative w-32 h-32">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="8"
                  className="text-muted/20"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="url(#gradient)"
                  strokeWidth="8"
                  strokeDasharray={2 * Math.PI * 45}
                  strokeDashoffset={(1 - overallScore / 100) * 2 * Math.PI * 45}
                  strokeLinecap="round"
                  className="transition-all duration-500"
                />
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#10b981" />
                    <stop offset="100%" stopColor="#3b82f6" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold text-foreground">{overallScore}%</span>
                <span className="text-xs text-muted-foreground">Achievement</span>
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">{entity.name}</h2>
              <p className="text-muted-foreground capitalize">{entity.entityType} BSC</p>
            </div>
          </div>

          {/* Perspective Gauges */}
          <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-6">
            {bscPerspectives.map(perspective => (
              <ScoreGauge 
                key={perspective.id}
                score={perspectiveScores[perspective.id] || 0}
                label={perspective.name}
                perspectiveColor={perspective.color}
              />
            ))}
          </div>
        </div>
      </SectionHeader>

      {/* Scorecard Table by Perspective */}
      {bscPerspectives.map(perspective => {
        const perspectiveObjectives = objectivesByPerspective[perspective.id] || [];
        const isExpanded = expandedPerspectives.includes(perspective.id);
        const Icon = iconMap[perspective.icon] || DollarSign;

        if (perspectiveObjectives.length === 0) return null;

        return (
          <Card key={perspective.id} className="overflow-hidden">
            {/* Perspective Header */}
            <div 
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/20 transition-colors"
              style={{ borderLeft: `4px solid ${perspective.color}` }}
              onClick={() => togglePerspective(perspective.id)}
              data-testid={`perspective-header-${perspective.id}`}
            >
              <div className="flex items-center gap-3">
                {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${perspective.color}20` }}
                >
                  <Icon className="w-5 h-5" style={{ color: perspective.color }} />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{perspective.name}</h3>
                  <p className="text-xs text-muted-foreground">{perspectiveObjectives.length} objectives</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <span className="text-2xl font-bold text-foreground">{perspectiveScores[perspective.id] || 0}%</span>
                  <p className="text-xs text-muted-foreground">Achievement</p>
                </div>
              </div>
            </div>

            {/* Table Header */}
            {isExpanded && (
              <>
                <div className="flex items-center py-2 px-4 bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wider border-y border-border/50">
                  <div className="w-8"></div>
                  <div className="flex-1 min-w-[200px]">Name</div>
                  <div className="w-20 text-center">Weight</div>
                  <div className="w-20 text-center">Bonus</div>
                  <div className="w-24 text-center">LY Actual</div>
                  <div className="w-24 text-center">Actual</div>
                  <div className="w-24 text-center">Target</div>
                  <div className="w-16 text-center">Status</div>
                  <div className="w-12 text-center">Trend</div>
                  <div className="w-24 text-center">Achievement</div>
                  <div className="w-12 text-center">Remarks</div>
                </div>

                {/* Objectives */}
                <div>
                  {perspectiveObjectives.map(objective => (
                    <ObjectiveRow 
                      key={objective.id}
                      objective={objective}
                      isExpanded={expandedObjectives.includes(objective.id)}
                      onToggle={() => toggleObjective(objective.id)}
                    />
                  ))}
                </div>
              </>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default CompanyScorecard;
